// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.ReportViewerContent.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { isEmpty, get, getOr, flow, concat, uniqBy } from 'lodash/fp';
import classnames from 'classnames';
import connect from 'share/enhancers/Izenda.Connect';
import ReportViewerFilterDescriptions from './Izenda.ReportViewerFilterDescriptions';
import ReportBody from 'modules/ReportDesigner/Fields/ReportBody/Izenda.ReportBody';
import ReportHeader from 'modules/ReportDesigner/Fields/Izenda.ReportHeader';
import ReportFooter from 'modules/ReportDesigner/Fields/Izenda.ReportFooter';
import ReportTitleAndDescription from 'modules/ReportDesigner/Fields/Izenda.ReportTitleAndDescription';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import { isSupportSticky } from 'share/utils/Izenda.BrowserUtils';

@connect({ toClass: true })
class ReportViewerContent extends Component {
  static displayName = 'ReportViewerContent';
  static propTypes = {
    store: PropTypes.object
  };

  static defaultProps = {
    className: 'content-container'
  };

  constructor(props: any) {
    
    super(props);
    this.isSupportSticky = isSupportSticky();
  }

  /**
   * Build list filter
   * @param {Array} filters
   */
  buildListFilter(filters: Array<any>) {
    if (isEmpty(filters)) {
      return null;
    }
    const indexStep = 1;
    return (
      <div className="u-center izenda-ReportViewer-FilterList">
        {filters.map(filter => (
          <p key={filter.index}>{`Filter #${filter.index + indexStep} - ${filter.filter.alias}`}</p>
        ))}
      </div>
    );
  }

  /**
   * Set ref for report header
   */
  setReportHeader = (ref: any): void => {
    this.reportHeader = ref;
  };

  /**
   * Set ref for report title
   */
  setReportTitle = (ref: any): void => {
    this.reportTitle = ref;
  };

  /**
   * Set ref for filter description
   */
  setFilterDescription = (ref: any): void => {
    this.reportFilterDescription = ref;
  };

  /**
   * Set ref for report body
   */
  setReportBody = (ref: any): void => {
    this.reportBody = ref;
  };

  /**
   * Set ref for report footer
   */
  setReportFooter = (ref: any): void => {
    this.reportFooter = ref;
  };

  /**
   * Set ref for outer scrollbox
   */
  setOuterScrollBox = (ref: any): void => {
    this.scrollBox = ref;
  };

  /**
   * Set ref for grid view scrollbox
   */
  setGridViewScrollBox = (ref: any): void => {
    if (ref) {
      this.gridViewScrollBox = ref.getGridViewScrollBox();
      this.handleStickyScroll();
    }
  };

  /**
   * Handle on outer scroll
   */
  onScroll = ({ target: { scrollLeft } }): void => {
    const { editMode } = this.props;
    if (!editMode && !this.isSupportSticky) {
      const setNewPosition = (elements: Array<any>) => {
        elements.forEach(element => {
          element && (element.style.left = `${scrollLeft}px`);
        });
      };
      setNewPosition([this.reportHeader, this.reportTitle, this.reportFilterDescription, this.reportFooter]);
    }
    if (editMode) {
      this.handleStickyScroll();
    }
  };

  /**
   * Handle sticky scroll
   */
  handleStickyScroll = (): void => {
    if (this.scrollBox && this.gridViewScrollBox) {
      const shouldStickScroll = this.checkShouldStickScroll();
      shouldStickScroll
        ? this.gridViewScrollBox.stickHorizontalScroll()
        : this.gridViewScrollBox.unstickHorizontalScroll();
    }
  };

  /**
   * Check content has outer scroll
   */
  checkHasOuterScroll(): void {
    
    const {
      store,
      store: { reportStore },
      editMode
    } = this.props;
    return (reportStore.isShowingHeaderFooter && store.isVisibleHeaderFooter) || !editMode;
  }

  /**
   * Check should stick horizontal scroll
   */
  checkShouldStickScroll(): void {
    if (this.checkHasOuterScroll() && this.scrollBox && this.gridViewScrollBox) {
      const scrollTop = this.scrollBox.getScrollTop();
      const bodyDOM = ReactDOM.findDOMNode(this.reportBody);
      const headerHeight = getOr(0, 'offsetHeight', this.reportHeader);
      const titleHeight = getOr(0, 'offsetHeight', this.reportTitle);
      const filterDesHeight = getOr(0, 'offsetHeight', this.reportFilterDescription);
      const trackHeight = this.scrollBox.getTrackHeight();
      return (
        headerHeight + titleHeight + filterDesHeight + bodyDOM.clientHeight + trackHeight >=
          this.props.viewerContentHeight + scrollTop && this.gridViewScrollBox.isInsideOfViewport()
      );
    }
    return false;
  }

  /**
   * Get scroll top value of outer scrollbox
   */
  getOuterScrollTop = () => {
    return this.scrollBox ? this.scrollBox.getScrollTop() : 0;
  };

  getInvalidFilters = (reportFilters: Array<any>): Array<any> => {
    const { emptyRequiredParamFilters, emptyNotVisibleRequiredFilter, emptyVisibleRequiredFilter } = reportFilters;
    const indexFieldMap = (filter, index) => ({ index, filter });
    return flow(
      concat(emptyNotVisibleRequiredFilter.map(indexFieldMap)),
      concat(emptyVisibleRequiredFilter.map(indexFieldMap)),
      uniqBy(item => item.filter.alias + item.index)
    )(emptyRequiredParamFilters.map(indexFieldMap));
  };

  render() {
    
    const {
      store,
      store: { reportFilters, reportStore },
      editMode,
      localization,
      className,
      isFullSize,
      bottom,
      viewerContentHeight
    } = this.props;
    const viewerContentClass = classnames(className, 'u-bgWhite', 'izenda-ReportViewer-FilterError');
    const isTitleDescriptionVisible = get('reportFormats.titleDescription.visible', store);
    const hasOuterScroll = this.checkHasOuterScroll();
    const style = viewerContentHeight ? { height: viewerContentHeight } : null;
    const reportFormatClassNames = classnames({
      'u-stickLeft': this.isSupportSticky,
      'u-posRelative': !this.isSupportSticky
    });
    const validatedFilters = this.getInvalidFilters(reportFilters);

    if (!isEmpty(validatedFilters)) {
      return (
        <div className="u_paddingLeftRight" ref={this.props.setViewerContentRef}>
          <div className={viewerContentClass} style={style}>
            <div className="u-center">
              {localization.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_SELECT_REQUIRE_AND_VISIBLE_FIELD')}
              {this.buildListFilter(validatedFilters)}
            </div>
          </div>
        </div>
      );
    }

    const content = (
      <ScrollBox
        fullWidth={editMode}
        viewClassName={classnames({ u_paddingLeftRight: editMode, 'viewer-mode': !editMode })}
        onScroll={this.onScroll}
        onUpdate={this.handleStickyScroll}
        ref={this.setOuterScrollBox}
      >
        {reportStore.isShowingHeaderFooter && (
          <div className={reportFormatClassNames} ref={this.setReportHeader}>
            <ReportHeader store={store} viewMode replaceSystemVariable={PrintingUtils.replaceSystemVariable} />
          </div>
        )}
        {isTitleDescriptionVisible && (
          <div className={reportFormatClassNames} ref={this.setReportTitle}>
            <ReportTitleAndDescription store={store} viewMode={true} />
          </div>
        )}
        <div className={reportFormatClassNames} ref={this.setFilterDescription}>
          <ReportViewerFilterDescriptions store={store} />
        </div>
        <div ref={this.setReportBody}>
          <ReportBody
            getOuterScrollTop={this.getOuterScrollTop}
            finishRender={this.setGridViewScrollBox}
            bottom={bottom}
            fullSize={true}
            store={store}
            viewMode={!editMode}
            quickEdit={!!editMode}
            hasOuterScroll={hasOuterScroll}
          />
        </div>
        {reportStore.isShowingHeaderFooter && (
          <div className={reportFormatClassNames} ref={this.setReportFooter}>
            <ReportFooter store={store} viewMode replaceSystemVariable={PrintingUtils.replaceSystemVariable} />
          </div>
        )}
      </ScrollBox>
    );

    return (
      <div className={className} style={style} ref={this.props.setViewerContentRef}>
        {editMode ? content : <div className="u_paddingLeftRight u_fullHeight">{content}</div>}
      </div>
    );
  }
}

export default ReportViewerContent;



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportViewer/Izenda.ReportViewerContent.js